import { Button } from '@jenmusic/jen-ui-library';
import SideBarGenerateIcon from '@/assets/icons/sideBarGenerate.svg';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import SuperVoiceBf from '@/assets/images/superVoice/sv-bg.png';
import { AudioUploader } from './components/AudioUploader';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'sonner';
import { useSessionStore } from '@/hooks/useSessionStore';
import { useRequest } from 'ahooks';
import { postSuperVoiceTrain, SuperVoiceTrainDTO } from '@/service/generatorv2';
import { useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import { useSuperVoiceList } from './hooks';

interface SuperVoiceFormValues {
  name: string;
  audioFiles: File[];
}

export const SuperVoiceCreate = () => {
  const navigate = useNavigate();

  const form = useForm<SuperVoiceFormValues>({
    defaultValues: {
      name: '',
      audioFiles: [],
    },
    mode: 'onChange',
  });

  const { handleSubmit, register, setValue, watch, control, formState } = form;
  const audioFiles = watch('audioFiles');
  const name = watch('name');

  const isFormValid = name.trim() !== '' && audioFiles.length > 0;

  const { run: runPostSuperVoiceTrain, loading: superVoiceTrainLoading } = useRequest(postSuperVoiceTrain, {
    manual: true,
    onSuccess: (data) => {
      toast.success('SuperVoice submitted successfully!');
      navigate('/super_voice');
    },
    onError: (error) => {
      toast.error(String(error) || 'Failed to submit SuperVoice. Please try again.');
    },
  });

  const onSubmit = (data: SuperVoiceFormValues) => {
    const formData = new FormData();

    formData.append('name', data.name);

    data.audioFiles.forEach((file, index) => {
      formData.append('files', file);
    });

    runPostSuperVoiceTrain(formData);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex-grow flex justify-center items-center">
        <Content name={name} register={register} control={control} />
      </div>
      <CreateFooter isFormValid={isFormValid} onSubmit={handleSubmit(onSubmit)} isSubmitting={superVoiceTrainLoading} />
    </div>
  );
};

interface ContentProps {
  register: any;
  control: any;
  name: string;
}

const Content = ({ name, register, control }: ContentProps) => {
  const { session } = useSessionStore();
  const { user } = session;
  return (
    <div className="p-8 w-[796px] h-[560px] bg-jenmusic-background-tertiary rounded-2xl flex justify-between">
      <div className="w-[50%]">
        <div className="w-[178px] typography-jenmusic-heading-large mb-6">SuperVoice Filter Beta️</div>
        <Label className="font-jenmusic-GT text-jenmusic-content-tertiary text-[12px] tracking-[-0.48px] uppercase" id="name">
          Supervoice Name
        </Label>
        <Input
          id="name"
          className="border border-jenmusic-border-background rounded-[8px] mt-1 h-12 mb-6"
          placeholder={'SuperVoice Name'}
          {...register('name', { required: 'please input SuperVoice name' })}
        />
        <Label className="font-jenmusic-GT text-jenmusic-content-tertiary text-[12px] tracking-[-0.48px] uppercase" id="upload">
          Upload base track
        </Label>
        <div className="mt-1">
          <Controller
            name="audioFiles"
            control={control}
            rules={{ required: true }}
            render={({ field }) => <AudioUploader minDuration={60} maxFiles={5} onChange={(files) => field.onChange(files)} />}
          />
        </div>
      </div>
      <div className="w-[40%] flex justify-center items-center">
        <div
          className="w-[232px] h-[372px] rounded-xl relative"
          style={{
            background: `url(${SuperVoiceBf})`,
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            boxShadow:
              '101.008px 158.919px 52.524px 0px rgba(0, 0, 0, 0.00), 64.645px 101.008px 48.484px 0px rgba(0, 0, 0, 0.03), 36.363px 56.565px 40.403px 0px rgba(0, 0, 0, 0.10), 16.161px 25.589px 29.629px 0px rgba(0, 0, 0, 0.17), 4.04px 6.734px 16.161px 0px rgba(0, 0, 0, 0.20)',
          }}
        >
          <div
            className="absolute rounded-[5px] top-[250px] left-[20px] w-[192px] h-[72px] flex flex-col justify-center items-center"
            style={{
              background: 'var(--Slot-Background, linear-gradient(0deg, #191A1B 0%, #1E2021 100%))',
            }}
          >
            <div className=" font-jenmusic-GT text-[12px] text-jenmusic-content-secondary tracking-[-0.36px] uppercase">
              {user.name || 'ARTIST/USER NAME'}
            </div>
            <div className=" font-jenmusic-GT text-[16px] text-[#fff] tracking-[-0.48px] uppercase"> {name || 'supervoice Name'}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface CreateFooterProps {
  isFormValid: boolean;
  onSubmit: () => void;
  isSubmitting: boolean;
}

const CreateFooter = ({ isFormValid, onSubmit, isSubmitting }: CreateFooterProps) => {
  return (
    <div className="h-[96px] p-6 flex justify-between typography-jenmusic-small">
      <div className="flex flex-col">
        <div className="flex w-[346px] justify-between">
          <div className="w-[206px] h-4 text-jenmusic-content-tertiary">Publish Status</div>
          <div className="text-jenmusic-content-secondary w-[140px]">Private</div>
        </div>
        <div className="flex w-[346px] justify-between">
          <div className="w-[206px] h-4 text-jenmusic-content-tertiary">Estimated Jeneration Time</div>
          <div className="text-jenmusic-content-secondary w-[140px]">60mins</div>
        </div>
      </div>
      <Button
        className="px-[18px] py-[14px] h-12 text-jenmusic-interactive-contrast"
        disabled={!isFormValid || isSubmitting}
        onClick={onSubmit}
      >
        <>
          <SideBarGenerateIcon fillColor="currentColor" />
          Jenerate Private SuperVoice<sup className="text-[8px]">TM</sup>
        </>
      </Button>
    </div>
  );
};
